// Hook pour vérifier les mises à jour de l'application
import { useState, useEffect, useCallback } from 'react';

const useVersionCheck = () => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [newVersion, setNewVersion] = useState(null);
  const [checking, setChecking] = useState(false);

  // Récupérer la version actuelle stockée en localStorage
  const getCurrentVersion = () => {
    return localStorage.getItem('app-version');
  };

  // Sauvegarder la version actuelle
  const setStoredVersion = (version) => {
    localStorage.setItem('app-version', version);
  };

  // Vérifier s'il y a une nouvelle version
  const checkForUpdates = useCallback(async () => {
    setChecking(true);
    try {
      // Ajouter un timestamp pour éviter le cache du fichier version.json
      const timestamp = new Date().getTime();
      const response = await fetch(`/react/version.json?t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const versionData = await response.json();
        const serverVersion = versionData.version;
        const storedVersion = getCurrentVersion();
        
        setCurrentVersion(storedVersion);
        setNewVersion(serverVersion);
        
        // Si on n'a pas de version stockée, on la définit
        if (!storedVersion) {
          setStoredVersion(serverVersion);
          setCurrentVersion(serverVersion);
        } else if (storedVersion !== serverVersion) {
          // Il y a une mise à jour disponible
          setHasUpdate(true);
        }
      }
    } catch (error) {
      console.warn('Erreur lors de la vérification de version:', error);
    } finally {
      setChecking(false);
    }
  }, []);

  // Recharger l'application avec la nouvelle version
  const updateApp = () => {
    if (newVersion) {
      setStoredVersion(newVersion);
      // Vider tous les caches du navigateur
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      // Recharger la page en forçant le rechargement depuis le serveur
      window.location.reload(true);
    }
  };

  // Ignorer cette mise à jour
  const dismissUpdate = () => {
    setHasUpdate(false);
    if (newVersion) {
      setStoredVersion(newVersion);
      setCurrentVersion(newVersion);
    }
  };

  // Vérifier automatiquement au chargement
  useEffect(() => {
    checkForUpdates();
    
    // Vérifier périodiquement (toutes les 5 minutes)
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [checkForUpdates]);

  // Vérifier aussi quand l'onglet redevient actif
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  return {
    hasUpdate,
    currentVersion,
    newVersion,
    checking,
    checkForUpdates,
    updateApp,
    dismissUpdate
  };
};

export default useVersionCheck;
